


import { LightningElement, api, track, createElement } from 'lwc';

import sldsMenuPopoverMenu from 'x/sldsMenuPopoverMenu';
import  {publicPropertyToBool} from '../../../utils/basicUtils.js'
import {positionMenuDropdownPopover, positionMenuDropdownPopoverV2} from '../../../utils/popoverUtils.js';
import { getLabelsByLanguage } from '../../../utils/languageUtil';

/* Example Usage:
        HTML:
            <x-slds-menu-popover-button 
                id={myButton.id}                  <-- add 'id' for multiple instances on the page
                options={rowActions}              <-- **required
                container={relativeContainer}     <-- *required if is in a relative container
                onselection={handleSelction}      <-- **required
                is-left="false"                   <-- default is false (pins menu to right side of button)
            >
                <button class="slds-button slds-button_icon slds-button_icon-border-filled slds-button_icon-x-small" 
                    aria-haspopup="true" 
                    tabindex="0" 
                    aria-selected="false"
                    title="More actions"
                    data-id={row.menuId}
                >
                    <svg class="slds-button__icon slds-button__icon_hint slds-button__icon_small" aria-hidden="true">
                        <use xlink:href="static/assets/icons/utility-sprite/svg/symbols.svg#down"></use>
                    </svg>
                    <span class="slds-assistive-text">More actions</span>
                </button>
            </x-slds-menu-popover-button>

        JavaScript:
            handleSelction(message) {
                // Get the response from <x-slds-menu-popover-button
                const detail= message.detail;

                // ***** detail can have id  ******
                if (typeof detail === 'object' && detail.id) {
                    const id = detail.id
                    const action = detail.selection

                // ***** detail without id  ******
                } else {
                    const selection = detail; 
                }
            }

*/


export default class sldsMenuPopoverButton extends LightningElement {
    static renderMode = 'light';
    @track navMode = 'mouse';

    @api menuSize;
    @api container;         // If button is in a relative/fixed/sticky container  
    @api id;               // If need an identifier for the button
    @api value            // current value of the of the menu
    @api 
    set options(val) {
        this._options = (val ?? []).map(opt => {
            return {...opt, label: getLabelsByLanguage(opt.label) ?? opt.label}
        });
    }
    get options() {
        return this._options;
    }
    @api                // menu starts at left edge of the button instead of right (default)
    set isLeft(value) {
        this._isLeft = publicPropertyToBool(value);
    }
    get isLeft() {
        return this._isLeft;
    }
    @api            // Add checkmark to selected menu option
    set isCheck(value) {
        this._isCheck = publicPropertyToBool(value);
    }
    get isCheck() {
        return this._isCheck;
    }

    // track the menu popover
    menuComponent;
    dropdownEl;
    menuItems;

    // Display popover menu when button clicked
    handleClick() {
        // toggle off if already open
        if (this.dropdownEl) {
            this.dropdownEl.remove();
            this.dropdownEl = null;
            return;
        }
    
        // 1) create the menu
        const menu = createElement('x-slds-menu-popover-menu', { is: sldsMenuPopoverMenu });
        menu.options = this.options;
        menu.isLeft = this.isLeft;
        menu.menuSize = this.menuSize;
        menu.value = this.value || '';
        menu.isCheck = this.isCheck;
        menu.id = this.id || null;
        menu.style.zIndex = '9999';
        menu.addEventListener('selection', (event) => {
            event.stopPropagation(); // prevent double events, just in case
            this.dispatchEvent(new CustomEvent('selection', {
                detail: event.detail,
                bubbles: true,
                composed: true
            }));
        });
        this.menuComponent = menu;
    
        // 2) append Menu into the container or body
        const host = this.container || document.body;
        host.appendChild(menu);
        this.dropdownEl = menu;
        this.menuItems = Array.from(menu.querySelectorAll('[data-element="menuItem"]'));
        this.dropdownEl.focus();
        this.menuItems[0].classList.add('hovered');

        // 3) Track menu mouse events
        this.menuItems.forEach(item => item.addEventListener('mouseover', this.handleMouseOver.bind(this)));
        this.menuItems.forEach(item => item.addEventListener('mouseleave', this.handleMouseLeave.bind(this)));
        // Set navMode to mouse when mouse moves in the menu
        menu.addEventListener('mousemove', this.handleMouseMove.bind(this));


        // 4) find the button
        const btn = this.querySelector('button');
        
        // 5) reposition helper
        //const reposition = () => { positionMenuDropdownPopover(btn, this.container, menu, this.isLeft); };
        const reposition = () => { positionMenuDropdownPopoverV2(btn, menu, this.isLeft); };
        // 6) initial position + listeners for scroll/resize
        reposition();
        window.addEventListener('resize', reposition, { passive: true });
        window.addEventListener('scroll', reposition, true);
    
        // 7) cleanup event listeners
        const cleanup = () => {
            // Clear resize/scroll listeneres
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
            
            // Clear close listeners (escape/outside mousedown)
            window.removeEventListener('mousedown', close);
            
            // Clear mouse listeners
            this.menuItems.forEach(item => item.removeEventListener('mouseover', this.handleMouseOver));
            this.menuItems.forEach(item => item.removeEventListener('mouseleave', this.handleMouseLeave));
            menu.removeEventListener('mousemove', this.handleMouseMove);
            
            // Clear selection listener
            menu.removeEventListener('selection', selectionHandler);
        };
    
        // 8) handle selection from menu
        const selectionHandler = e => {
            cleanup();
            menu.remove();
            this.menuItems = null;
            this.dropdownEl = null;
        };
        menu.addEventListener('selection', selectionHandler);
    
        // 9) auto-close on outside-mousedown/Escape-press 
        const close = e => {
            if (!menu.contains(e.target) && !btn.contains(e.target) ) {
                cleanup();
                menu.remove();
                this.dropdownEl = null;
            }
        };
        window.addEventListener('mousedown', close);
    }

    // Set navMode to 'mouse' (navMode prevents hovers from stealing focus from key navigation)
    handleMouseMove() {
        if (this.navMode !== 'mouse') {
            this.navMode = 'mouse';
        }
    };
    // Check that a dataset.value is associated with the mouseover/mouseleave event
    isValidMouseEvt(event) {
        return this.navMode === 'mouse' && event.currentTarget.dataset && event.currentTarget.dataset.value;
    }
    // Add '.hovered' class when mouse hovers over list item
    handleMouseOver(event) {
        if ( this.isValidMouseEvt(event) ) {
            const idx = this.menuItems.findIndex(men => men.dataset.value === event.currentTarget.dataset.value);
            this.hoverMenuItem(idx);
        };
    }
    // Remove '.hovered' class from list item
    handleMouseLeave(event) {
        if ( !this.isValidMouseEvt(event) ) return;
        event.currentTarget.classList.remove('hovered');
    }

    // Add gray background ('.hovered') to the index of list item
    hoverMenuItem(idx) {
        this.menuItems.forEach(item =>  item.classList.remove('hovered') );
        this.menuItems[idx].classList.add('hovered');
        this.menuItems[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Handle ArrowUp/ArrowDown/Enter Key Events
    handleKeyDown(event) {
        if (!this.dropdownEl) return;
        if (this.navMode !== 'keyboard') {
            this.navMode = 'keyboard';
        }
        // Handle Arrow keys menu navigation
        if (event.key && (event.key === 'ArrowUp' || event.key==='ArrowDown')) {
            event.preventDefault();
            event.stopPropagation();
            
            // Currently hovered item
            const hoverdIdx = this.menuItems.findIndex(ele => ele.classList.contains('hovered'));

            if (event.key === 'ArrowUp') {
                hoverdIdx === -1 || hoverdIdx === 0
                    ? this.hoverMenuItem(this.menuItems.length-1)  // hover last item
                    : this.hoverMenuItem(hoverdIdx-1);     // hover <hoverdIdx>-1
            } else {
                hoverdIdx === -1 || hoverdIdx === this.menuItems.length-1
                    ? this.hoverMenuItem(0)               // hover first item
                    : this.hoverMenuItem(hoverdIdx+1);   // hover <hoverdIdx>+1
            }
            return;
        } 

        if (event.key === 'Escape' || event.key === 'Tab') {
            if (this.dropdownEl) {
                this.dropdownEl.remove();
                this.dropdownEl = null;
            }
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            const selectedValue = this.menuItems.find(menu => menu.classList.contains('hovered'))?.dataset?.value;
            if (selectedValue) {
                this.menuComponent.dispatchSelection(selectedValue);
            }
        }
    }

    handleBlur() {
        if (this.dropdownEl) {  
            this.dropdownEl.remove();
            this.dropdownEl = null;
        }
    }
    
}